// client/src/components/shared/LegalAccordion.js
// Accordéon affichant un document légal inline avec checkbox d'acceptation.
// Usage :
//   <LegalAccordion docId="cgu" checked={acceptedCGU} onChange={setAcceptedCGU} />

import React, { useState, useEffect } from 'react';
import { FiChevronDown } from 'react-icons/fi';

const DOC_FILES = {
  'cgu':                    'conditions-generales-utilisation.md',
  'cgv':                    'conditions-generales-vente.md',
  'conditions-affilies':    'conditions-affilies.md',
  'politique-confidentialite': 'politique-confidentialite-rgpd.md',
};

const DOC_LABELS = {
  'cgu':                    "Conditions générales d'utilisation",
  'cgv':                    'Conditions générales de vente',
  'conditions-affilies':    'Conditions affiliés',
  'politique-confidentialite': 'Politique de confidentialité',
};

// ─── Rendu markdown minimaliste ────────────────────────────────────────────
function parseInline(text) {
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**'))
      return <strong key={idx}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`'))
      return <code key={idx} style={{ background: 'var(--eb-surface-2)', border: '1px solid var(--eb-border)', borderRadius: 4, padding: '1px 4px', fontFamily: 'var(--eb-font-mono)', fontSize: '0.9em' }}>{part.slice(1, -1)}</code>;
    const urlRegex = /(https?:\/\/[^\s)]+)/g;
    if (urlRegex.test(part)) {
      return part.split(/(https?:\/\/[^\s)]+)/g).map((up, ui) =>
        /^https?:\/\//.test(up)
          ? <a key={ui} href={up} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--eb-brand)' }}>{up}</a>
          : up
      );
    }
    return part;
  });
}

function renderMarkdown(md) {
  const lines = md.split('\n');
  const els = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^---+$/.test(line.trim())) {
      els.push(<hr key={i} style={{ border: 'none', borderTop: '1px solid var(--eb-border)', margin: '12px 0' }} />);
      i++; continue;
    }
    if (line.includes('|') && lines[i + 1] && /^\|[-| :]+\|/.test(lines[i + 1])) {
      const tLines = [];
      while (i < lines.length && lines[i].includes('|')) { tLines.push(lines[i]); i++; }
      const headers = tLines[0].split('|').filter((_, ci) => ci > 0 && ci < tLines[0].split('|').length - 1);
      const rows    = tLines.slice(2).map(r => r.split('|').filter((_, ci) => ci > 0 && ci < r.split('|').length - 1));
      els.push(
        <div key={`t${i}`} style={{ overflowX: 'auto', margin: '10px 0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88em' }}>
            <thead><tr style={{ background: 'var(--eb-surface-2)' }}>
              {headers.map((h, hi) => <th key={hi} style={{ padding: '6px 10px', border: '1px solid var(--eb-border)', textAlign: 'left', fontWeight: 600, color: 'var(--eb-text-2)' }}>{parseInline(h.trim())}</th>)}
            </tr></thead>
            <tbody>{rows.map((row, ri) => (
              <tr key={ri} style={{ background: ri % 2 === 0 ? 'var(--eb-surface)' : 'var(--eb-surface-2)' }}>
                {row.map((cell, ci) => <td key={ci} style={{ padding: '5px 10px', border: '1px solid var(--eb-border)', verticalAlign: 'top' }}>{parseInline(cell.trim())}</td>)}
              </tr>
            ))}</tbody>
          </table>
        </div>
      );
      continue;
    }
    if (line.startsWith('#### ')) els.push(<h4 key={i} style={{ fontSize: '0.95em', fontWeight: 600, margin: '12px 0 4px', color: 'var(--eb-text)' }}>{parseInline(line.slice(5))}</h4>);
    else if (line.startsWith('### ')) els.push(<h3 key={i} style={{ fontSize: '1em', fontWeight: 600, margin: '14px 0 6px', color: 'var(--eb-text)' }}>{parseInline(line.slice(4))}</h3>);
    else if (line.startsWith('## '))  els.push(<h2 key={i} style={{ fontSize: '1.1em', fontWeight: 700, margin: '18px 0 8px', color: 'var(--eb-brand-800)', borderBottom: '1px solid var(--eb-border)', paddingBottom: 4 }}>{parseInline(line.slice(3))}</h2>);
    else if (line.startsWith('# '))   els.push(<h1 key={i} style={{ fontSize: '1.3em', fontWeight: 700, margin: '0 0 14px', color: 'var(--eb-text)' }}>{parseInline(line.slice(2))}</h1>);
    else if (/^[-*] /.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*] /.test(lines[i])) { items.push(<li key={i} style={{ marginBottom: 3, color: 'var(--eb-text-2)' }}>{parseInline(lines[i].slice(2))}</li>); i++; }
      els.push(<ul key={`ul${i}`} style={{ margin: '6px 0 6px 18px', padding: 0, color: 'var(--eb-text-2)' }}>{items}</ul>);
      continue;
    }
    else if (line.trim() === '') els.push(<div key={i} style={{ height: 6 }} />);
    else els.push(<div key={i} style={{ margin: '4px 0', lineHeight: 1.6, fontSize: '0.92em', color: 'var(--eb-text-2)' }}>{parseInline(line)}</div>);
    i++;
  }
  return els;
}
// ───────────────────────────────────────────────────────────────────────────

export default function LegalAccordion({ docId, checked, onChange }) {
  const [open, setOpen]       = useState(false);
  const [content, setContent] = useState(null);
  const [error, setError]     = useState(false);

  const label = DOC_LABELS[docId] || docId;

  useEffect(() => {
    if (!open || content !== null || error) return;
    const file = DOC_FILES[docId];
    if (!file) { setError(true); return; }
    fetch(`${process.env.PUBLIC_URL}/legal/${file}`)
      .then(r => { if (!r.ok) throw new Error(); return r.text(); })
      .then(setContent)
      .catch(() => setError(true));
  }, [open, docId, content, error]);

  const checkboxId = `legal-accept-${docId}`;

  return (
    <div
      style={{
        border: `1px solid ${checked ? '#c6f0da' : 'var(--eb-border)'}`,
        borderRadius: 'var(--eb-radius)',
        marginBottom: 12,
        overflow: 'hidden',
        background: checked ? 'var(--eb-success-bg)' : 'var(--eb-surface-2)',
        textAlign: 'left',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
      }}
    >

      {/* ── Ligne unique : checkbox + texte + flèche ── */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '10px 8px 10px 14px', gap: 12, minWidth: 0 }}>
        {/* Checkbox — reset complet des styles signup.css */}
        <input
          type="checkbox"
          id={checkboxId}
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          style={{
            flexShrink: 0,
            width: 18, height: 18,
            minWidth: 18, minHeight: 0,
            padding: 0, margin: 0, marginBottom: 0,
            border: 'revert',
            borderRadius: 0,
            boxShadow: 'none',
            boxSizing: 'content-box',
            cursor: 'pointer',
            accentColor: 'var(--eb-brand)',
          }}
        />
        {/* Texte — on évite <label> pour échapper à .signup-container label */}
        <span
          onClick={() => onChange(!checked)}
          style={{ flex: 1, minWidth: 0, cursor: 'pointer', fontSize: 13.5, color: 'var(--eb-text-2)', userSelect: 'none', lineHeight: 1.4 }}
        >
          J'accepte les<br /><strong style={{ color: 'var(--eb-brand-800)', fontWeight: 600, display: 'block' }}>{label}</strong>
        </span>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          style={{
            flexShrink: 0, width: 40, height: 40, minHeight: 0, padding: 0, margin: 0,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none', borderRadius: 8, boxShadow: 'none', cursor: 'pointer',
            color: 'var(--eb-muted)', fontSize: 18, lineHeight: 1,
          }}
          aria-label={open ? 'Réduire' : 'Lire le document'}
          aria-expanded={open}
          title={open ? 'Réduire' : 'Lire le document'}
        >
          <FiChevronDown style={{ transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'none' }} />
        </button>
      </div>

      {/* ── Contenu déroulant ── */}
      {open && (
        <div
          spellCheck={false}
          style={{
            maxHeight: 320, overflowY: 'auto', padding: '14px 16px',
            background: 'var(--eb-surface)', borderTop: '1px solid var(--eb-border)',
            color: 'var(--eb-text-2)', fontSize: 14,
          }}
        >
          {error     && <div style={{ color: 'var(--eb-danger)', fontSize: 13.5 }}>Document temporairement indisponible.</div>}
          {!error && content === null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--eb-muted)', fontSize: 13.5 }}>
              <span className="eb-spinner eb-spinner--sm" aria-hidden="true" /> Chargement…
            </div>
          )}
          {!error && content !== null && renderMarkdown(content)}
        </div>
      )}
    </div>
  );
}
